import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useFetch, PageHeader, Panel, StatusBadge, Table, Empty, Loading } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = { name: "", description: "", min_amount: 50, max_amount: 500, daily_percent: 1, duration_days: 30, active: true };

function PlanDialog({ plan, onClose, onDone }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (plan) setF(plan.id ? plan : BLANK);
  }, [plan]);
  if (!plan) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, min_amount: Number(f.min_amount), max_amount: Number(f.max_amount), daily_percent: Number(f.daily_percent), duration_days: Number(f.duration_days) };
    try {
      if (plan.id) await api.put(`/admin/plans/${plan.id}`, body);
      else await api.post("/admin/plans", body);
      toast.success("Plan saved");
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const num = (k, l, step = "0.01") => (
    <div><label className="nx-label">{l}</label><input type="number" step={step} required className="nx-input num" value={f[k]} onChange={set(k)} data-testid={`plan-${k}-input`} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121722] border-[#242F42] text-white" data-testid="plan-dialog">
        <DialogHeader><DialogTitle className="font-display">{plan.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="nx-label">Name</label><input required className="nx-input" value={f.name} onChange={set("name")} data-testid="plan-name-input" /></div>
          <div><label className="nx-label">Description</label><input className="nx-input" value={f.description || ""} onChange={set("description")} data-testid="plan-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">{num("min_amount", "Min amount (USD)")}{num("max_amount", "Max amount (USD)")}</div>
          <div className="grid grid-cols-2 gap-4">{num("daily_percent", "Daily return %")}{num("duration_days", "Duration (days)", "1")}</div>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" className="accent-[#10B981]" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="plan-active-checkbox" />Visible to users</label>
          <p className="text-[11px] text-slate-500">Rate changes apply to new activations; existing investments keep their locked-in rate.</p>
          <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="plan-save-btn">{busy && <Loader2 size={16} className="animate-spin" />}Save plan</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPlans() {
  const { data, loading, reload } = useFetch("/admin/plans");
  const { data: invs } = useFetch("/admin/investments?status=active");
  const [edit, setEdit] = useState(null);
  const del = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try {
      const { data: r } = await api.delete(`/admin/plans/${p.id}`);
      toast.success(r.deactivated ? "Plan has active investments — deactivated instead" : "Plan deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="Plans & daily returns" subtitle="Daily % per plan is credited automatically every day at 00:05 UTC."
        actions={<button className="nx-btn-primary" onClick={() => setEdit({})} data-testid="new-plan-btn"><Plus size={16} />New plan</button>} />
      <Panel title="Trading plans">
        {loading ? <Loading /> : (
          <Table head={["Name", "Range", "Daily %", "Duration", "Status", ""]} testid="admin-plans-table">
            {!data?.length && <Empty cols={6} text="No plans" />}
            {data?.map((p) => (
              <tr key={p.id} data-testid={`admin-plan-row-${p.id}`}>
                <td className="nx-td text-white">{p.name}<p className="text-xs text-slate-500">{p.description}</p></td>
                <td className="nx-td num">{usd(p.min_amount)} – {usd(p.max_amount)}</td>
                <td className="nx-td num text-[#10B981]">{p.daily_percent}%</td>
                <td className="nx-td num">{p.duration_days} days</td>
                <td className="nx-td"><StatusBadge status={p.active ? "active" : "cancelled"} /></td>
                <td className="nx-td text-right">
                  <button onClick={() => setEdit(p)} className="mr-3 text-slate-400 hover:text-[#10B981]" data-testid={`edit-plan-${p.id}`}><Pencil size={15} /></button>
                  <button onClick={() => del(p)} className="text-slate-400 hover:text-red-400" data-testid={`delete-plan-${p.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      <Panel title="Active investments">
        <Table head={["Started", "User ID", "Plan", "Amount", "Rate", "Days", "Earned", "Last paid"]} testid="admin-investments-table">
          {!invs?.length && <Empty cols={8} text="No active investments" />}
          {invs?.map((v) => (
            <tr key={v.id}>
              <td className="nx-td text-xs">{fmtDate(v.created_at)}</td>
              <td className="nx-td num text-[#10B981]">{v.user_code}</td>
              <td className="nx-td">{v.plan_name}</td>
              <td className="nx-td num">{usd(v.amount)}</td>
              <td className="nx-td num">{v.daily_percent}%</td>
              <td className="nx-td num">{v.days_paid}/{v.duration_days}</td>
              <td className="nx-td num text-[#10B981]">{usd(v.total_earned)}</td>
              <td className="nx-td num text-xs">{v.last_paid_date || "—"}</td>
            </tr>
          ))}
        </Table>
      </Panel>
      <PlanDialog plan={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); reload(); }} />
    </div>
  );
}
